import { Injectable } from '@angular/core';
import { Appointment, MonthlyScheduleRecord } from './models';
import { SCHEDULE_TASK_TYPE, TaskType } from './../tasks/models/task-type.enum';
import {
    getSafeProp,
    isDateMidnight,
} from '../common/util';
import { convertValueToLuxon } from '@common/util/date-time-formatting/convert-value-to-luxon';
import { animals, births, calendar, clinical, housing, IconContent, lines, mating, pin } from '@icons';
import { Entity, Resource, ScheduleNonTask, TaskInstance } from '@common/types';
import { DailyScheduleRecord } from './models/daily-schedule-record';
import { TaskInstanceService } from '@services/task-instance.service';
import { AuthService } from '@services/auth.service';
import { WorkflowService } from '../workflow/services/workflow.service';

/**
 * Logic helper class for the ScheduleFacetComponent
 */
@Injectable()
export class ScheduleHelperService {

    readonly UNASSIGNED_LABEL = 'Unassigned';
    readonly UNASSIGNED_FOREGROUND_COLOR = '#000000';
    readonly UNASSIGNED_BACKGROUND_COLOR = '#c0c0c0';
    readonly GROUP_BACKGROUND_COLOR = '#FFFFFF';

    readonly = true;

    constructor(
        private taskInstanceService: TaskInstanceService,
        private authService: AuthService,
        private workflowService: WorkflowService
    ) { }

    setReadonly(readonly: boolean): void {
        this.readonly = readonly;
    }

    updateNonTask(appointments: Appointment[], scheduleNonTask: ScheduleNonTask, newData: Appointment): void {
        scheduleNonTask.DateTimeStart = new Date(newData.startDate);
        scheduleNonTask.DateTimeComplete = new Date(newData.endDate);
        // Need to replace the appointment to avoid an update loop
        this.replaceAppointmentsFromNonTasks(appointments, [scheduleNonTask]);
    }

    replaceAppointmentsFromNonTasks(
        appointments: Appointment[], scheduleNonTasks: ScheduleNonTask[]
    ): void {
        for (const scheduleNonTask of scheduleNonTasks) {
            const appointment = this.findAppointmentByNonTask(appointments, scheduleNonTask);
            if (appointment) {
                const index = appointments.indexOf(appointment);
                appointments[index] =
                    this.createAppointmentFromNonTask(scheduleNonTask);
            }
        }
    }

    removeNonTaskAppointment(appointments: Appointment[], scheduleNonTask: ScheduleNonTask): void {
        const appointment = this.findAppointmentByNonTask(appointments, scheduleNonTask);
        this.removeAppointment(appointments, appointment);
    }

    private removeAppointment(appointments: Appointment[], appointment: Appointment) {
        const index = appointments.indexOf(appointment);
        if (index >= 0) {
            appointments.splice(index, 1);
        }
    }

    findAppointmentByNonTask(appointments: Appointment[], scheduleNonTask: ScheduleNonTask): Appointment {
        return appointments.find((item) => {
            const nonTaskKey = getSafeProp(item, 'scheduleNonTask.C_ScheduleNonTask_key');
            return nonTaskKey === scheduleNonTask.C_ScheduleNonTask_key;
        });
    }

    createGroupedTaskAppointments(records: MonthlyScheduleRecord[]): Appointment[] {
        const appointments: Appointment[] = [];

        for (const record of records) {
            const appointment = this.createGroupedTaskAppointment(record);
            appointments.push(appointment);
        }

        return appointments;
    }

    private createGroupedTaskAppointment(record: MonthlyScheduleRecord): Appointment {
        const resourceGrouped = record.ResourceGrouped === 'True';
        const getResourceBackgroundColor = (backgroundColor: string) => {
            if (resourceGrouped) {
                return this.GROUP_BACKGROUND_COLOR;
            }
            return backgroundColor || this.UNASSIGNED_BACKGROUND_COLOR;
        };
        const getResourceForegroundColor = (foregroundColor: string) => {
            return resourceGrouped || !foregroundColor ? this.UNASSIGNED_FOREGROUND_COLOR : foregroundColor;
        };

        return {
            taskName: record.TaskName,
            startDate: convertValueToLuxon(record.DateStart).toUTC().toISO(),
            endDate: convertValueToLuxon(record.DateEnd).plus({ minutes: 1 }).toUTC().toISO(),
            allDay: true,

            assignedToName: record.ResourceName || this.UNASSIGNED_LABEL,
            taskType: record.TaskType,
            icon: this.getIconImage(record.TaskType),
            animalCount: record.AnimalCount,
            taskCount: record.TaskCount,
            resourceBackgroundColor: getResourceBackgroundColor(record.ResourceBackgroundColor),
            resourceForegroundColor: getResourceForegroundColor(record.ResourceForegroundColor),
            resourceGrouped,
            location: record.CurrentLocationPath,
            jobName: record.JobID,
        };
    }

    async createIndividualTaskAppointments(tasks: DailyScheduleRecord[], taskInstancesByKeyMap: Map<number, Entity<TaskInstance>>): Promise<Appointment[]> {
        const appointments: Appointment[] = [];


        for (const task of tasks) {
            const taskInstances: Entity<TaskInstance>[] = [];
            const taskInstanceKeys = task.TaskKeys.split(',').map(key => Number(key))

            for (const key of taskInstanceKeys) {
                const taskInstance = taskInstancesByKeyMap.get(key)

                if (taskInstance) {
                    taskInstances.push(taskInstance)
                }
            }

            appointments.push(this.createAppointmentFromTask(task, taskInstances, taskInstanceKeys));
        }

        return appointments;
    }


    createAppointmentFromTask(
        task: DailyScheduleRecord, taskInstances: Entity<TaskInstance>[], taskInstanceKeys: number[]
    ): Appointment {

        const appointment: Appointment = {
            taskInstances,
            taskInstanceKeys,
            taskAlias: task.TaskAlias,
            taskName: task.TaskName,
            startDate: convertValueToLuxon(task.DateStart).setZone('utc', { keepLocalTime: true }).toISO(),
            endDate: convertValueToLuxon(task.DateEnd).setZone('utc', { keepLocalTime: true }).toISO(),
            assignedToId: task.ResourceId,
            assignedToName: task.ResourceName ?? this.UNASSIGNED_LABEL,
            taskType: task.TaskType,
            icon: this.getIconImage(task.TaskType),
            animalCount: task.AnimalCount,
            taskCount: task.TaskCount,
            isLocked: task.IsLocked,
            location: task.TaskLocation ?? '',
            jobName: task.JobId,
            duration: taskInstances[0]?.Duration,
            taskStatusKey: taskInstances[0]?.C_TaskStatus_key,
            resourceGrouped: task.ResourceGrouped,
            resourceBackgroundColor: task.ResourceGrouped ? this.GROUP_BACKGROUND_COLOR : (task.ResourceBackgroundColor || this.UNASSIGNED_BACKGROUND_COLOR),
            resourceForegroundColor: task.ResourceGrouped ? this.UNASSIGNED_FOREGROUND_COLOR : (task.ResourceForegroundColor || this.UNASSIGNED_FOREGROUND_COLOR),
            effort: task.Effort,
            canDelete: task.TaskType === SCHEDULE_TASK_TYPE && !this.readonly,
            isGroup: task.IsGroup
        };

        if (appointment.startDate) {
            if (convertValueToLuxon(appointment.startDate).toFormat("HH:mm:ss") === '00:00:00') {
                const minutesInDay = 24 * 60;
                appointment.endDate = convertValueToLuxon(task.DateStart).plus({ "minutes": minutesInDay }).setZone('utc', { keepLocalTime: true }).toISO();
            }
        }

        return appointment;
    }

    createNonTaskAppointments(scheduleNonTasks: ScheduleNonTask[]): Appointment[] {
        const appointments: Appointment[] = [];

        for (const scheduleNonTask of scheduleNonTasks) {
            appointments.push(this.createAppointmentFromNonTask(scheduleNonTask));
        }

        return appointments;
    }

    createAppointmentFromNonTask(scheduleNonTask: ScheduleNonTask): Appointment {
        const resource = scheduleNonTask.Resource;
        const isAllDay = isDateMidnight(scheduleNonTask.DateTimeStart);


        const appointment: Appointment = {
            scheduleNonTask,
            taskAlias: scheduleNonTask.Description,
            taskName: scheduleNonTask.Description,
            taskType: SCHEDULE_TASK_TYPE,
            icon: this.getIconImage(SCHEDULE_TASK_TYPE),
            startDate: convertValueToLuxon(scheduleNonTask.DateTimeStart).toUTC().toISO(),
            endDate: convertValueToLuxon(scheduleNonTask.DateTimeComplete).toUTC().toISO(),
            allDay: isAllDay,
            canDelete: !this.readonly,
            assignedToName: resource ? resource.ResourceName : this.UNASSIGNED_LABEL,
            // assigned via assignResourceStyleProperties()
            resourceBackgroundColor: null,
            resourceForegroundColor: null,
            resourceGrouped: null,
            effort: null,
            taskCount: 1,
        };

        this.assignResourceStyleProperties(appointment, resource);

        return appointment;
    }


    private assignResourceStyleProperties(appointment: Appointment, resource: Resource | null) {
        if (resource) {
            if (resource.IsGroup) {
                // Multiple resources assigned.
                appointment.resourceGrouped = true;
                appointment.resourceBackgroundColor = this.GROUP_BACKGROUND_COLOR;
                appointment.resourceForegroundColor = this.UNASSIGNED_FOREGROUND_COLOR;

            } else {
                // Just one resource assigned
                appointment.resourceBackgroundColor = resource.BackgroundColor;
                appointment.resourceForegroundColor = resource.ForegroundColor;
            }
        } else {
            // Not assigned yet
            appointment.resourceBackgroundColor = this.UNASSIGNED_BACKGROUND_COLOR;
            appointment.resourceForegroundColor = this.UNASSIGNED_FOREGROUND_COLOR;
        }
    }

    private getIconImage(taskType: string): IconContent {
        switch (taskType) {
            case TaskType.Job: return pin;
            case TaskType.Animal: return animals;
            case TaskType.Birth: return births;
            case TaskType.Housing: return housing;
            case TaskType.Mating: return mating;
            case TaskType.Line: return lines;
            case TaskType.HealthRecord: return clinical;
            // for ScheduleNonTask items
            case SCHEDULE_TASK_TYPE: return calendar;
            default: return null;
        }
    }

    async dueDateChanged(taskInstanceKeys: number[], date: Date, logTag: string, reset?: () => void): Promise<number[]> {
        try {
            const updateParams: {
                TaskInstanceKey: number;
                DateDue?: Date
            }[] = []

            for (const taskInstanceKeyStr of taskInstanceKeys) {
                const taskInstanceKey = Number(taskInstanceKeyStr);

                if (isNaN(taskInstanceKey)) {
                    continue;
                }

                updateParams.push({
                    TaskInstanceKey: taskInstanceKey,
                    DateDue: date,
                })
            }

            return this.taskInstanceService.taskBulkDueDateChange({
                TaskDateDues: updateParams,
                ModifiedBy: this.authService.getCurrentUserName(),
                source: logTag,
            });
        } catch {
            reset?.();
        }
    }
}
